import { useId } from 'react';
import type { ChangeEvent, ReactElement } from 'react';
import { cn } from './cn';

export interface SliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  /** Appended to the readout and used to build `aria-valuetext`. */
  unit?: string;
  /** Overrides the readout entirely; also drives `aria-valuetext`. */
  format?: (value: number) => string;
  hideValue?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export function Slider({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit,
  format,
  hideValue = false,
  disabled = false,
  id,
  className,
}: SliderProps): ReactElement {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const readout = format ? format(value) : unit ? `${value}${unit}` : String(value);

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    onChange(event.target.valueAsNumber);
  }

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={inputId} className="text-[13px] font-medium text-ink">
          {label}
        </label>
        {!hideValue && (
          <span aria-hidden="true" className="font-mono text-xs tabular-nums text-ink-muted">
            {readout}
          </span>
        )}
      </div>
      <input
        id={inputId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={handleChange}
        // Only worth overriding when the raw number is not the whole story.
        aria-valuetext={unit !== undefined || format !== undefined ? readout : undefined}
        className={cn(
          'h-11 w-full cursor-pointer accent-accent',
          disabled && 'cursor-not-allowed opacity-55',
        )}
      />
    </div>
  );
}
